import { createAsyncThunk } from "@reduxjs/toolkit";
import { instance } from "./../../axios/axios";

// Buster Call feature (Levi, thread 1a0a16be9739464e).
// All routes require a bearer token — matches the glitch thunks' pattern.

const reject = (error, rejectWithValue) => {
  if (error.response && error.response.data?.message) {
    return rejectWithValue(error.response.data.message);
  }
  return rejectWithValue(error.message);
};

const authHeaders = (getState) => ({
  headers: { Authorization: `Bearer ${getState().auth.token}` },
});

// Submit a new Buster Call (President-only)
export const submitBusterCall = createAsyncThunk(
  "busterCall/submit",
  async ({ companyName, reviewLink, cause, desiredRating }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token;
      const { data } = await instance.post(
        "/buster-call/portal/submit",
        { companyName, reviewLink, cause, desiredRating },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// List all past Buster Calls
export const fetchBusterCalls = createAsyncThunk(
  "busterCall/list",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.get("/buster-call/portal/list", authHeaders(getState));
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Submit "Answer The Call" proof (optional file upload)
export const answerBusterCall = createAsyncThunk(
  "busterCall/answer",
  async ({ id, file }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token;
      const formData = new FormData();
      if (file) formData.append("proof", file);
      const { data } = await instance.post(
        `/buster-call/portal/${id}/answer`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// "Be Lame" — the employee's deliberate choice NOT to leave the review.
//
// This is the ONLY way to close a Buster Call banner: the X on the row expands
// to the "Show Support .. or Be Lame" prompt instead of dismissing, and the
// second Be Lame tap lands here. It must go to a buster-call route rather than
// the generic dismissNotification, because the banner row the server injects for
// an unanswered call carries a synthetic id ("buster_call_inject_7") that the
// generic dismiss endpoint cannot resolve — the tap used to be a silent no-op
// and the banner reappeared on the next ~8s poll. The server now records the
// decline and stops re-injecting that call for this employee.
// Idempotent server-side: tapping Be Lame again is not an error.
// (Levi, thread 1a10cf494be4ccac.)
export const beLameBusterCall = createAsyncThunk(
  "busterCall/decline",
  async ({ id }, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.post(
        `/buster-call/portal/${id}/decline`,
        {},
        authHeaders(getState)
      );
      return { ...data, callId: id };
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Buster Call answer leaderboard
export const fetchBusterCallLeaderboard = createAsyncThunk(
  "busterCall/leaderboard",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.get("/buster-call/portal/leaderboard", authHeaders(getState));
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// All pending proof submissions awaiting approval (President-only)
export const fetchPendingBusterCallAnswers = createAsyncThunk(
  "busterCall/pendingAnswers",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.get("/buster-call/portal/pending-answers", authHeaders(getState));
      return data;
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Approve a pending proof submission (President-only)
export const approveBusterCallAnswer = createAsyncThunk(
  "busterCall/approveAnswer",
  async ({ answerId }, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.post(
        `/buster-call/portal/answers/${answerId}/approve`,
        {},
        authHeaders(getState)
      );
      return { ...data, answerId };
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);

// Reject a pending proof submission (President-only)
export const rejectBusterCallAnswer = createAsyncThunk(
  "busterCall/rejectAnswer",
  async ({ answerId }, { rejectWithValue, getState }) => {
    try {
      const { data } = await instance.post(
        `/buster-call/portal/answers/${answerId}/reject`,
        {},
        authHeaders(getState)
      );
      return { ...data, answerId };
    } catch (error) {
      return reject(error, rejectWithValue);
    }
  }
);
